import { TextField, Typography } from '@mui/material'
import { Box, LabraToolTip } from '@labrav/react-components'
import { InfoOutlined } from '@mui/icons-material'
import { useStyles } from './ResaleAuthorizationDetails.styles'

import React, { useMemo, useState, useEffect } from 'react'
import clsx from 'clsx'
import { ResaleAuthorizationWithErrors } from '../../../../modules/resaleAuthorization/types'
import { AddRecipients } from '../../../PrivateOfferForm/Recipients/Recipients'
import { getRecipientErrMapping } from '../../../PrivateOfferForm/Details/utils'
import { KeyValue } from '../../../../modules/privateOffer/privateOfferCreation/types'
import { isEmpty } from 'lodash'
import { getRandom } from '@labrav/common'

export type DetailsProps = {
  resaleAuthorizationWithError: ResaleAuthorizationWithErrors
  addOfferRecipient: (key: string) => void
  deleteOfferRecipient: (index: number, key: string) => () => void
  updateRecipientsData: (index: number, key: string) => (data: KeyValue) => void
  handleDetailsChange: (data: KeyValue) => void
}
export const ResaleAuthorizationDetails = React.memo(
  ({
    resaleAuthorizationWithError,
    addOfferRecipient,
    deleteOfferRecipient,
    updateRecipientsData,
    handleDetailsChange,
  }: DetailsProps) => {
    const { classes } = useStyles()
    const [randomKey, setRandomKey] = useState<number>(getRandom())
    useEffect(() => {
      setRandomKey(getRandom())
    }, [])
    const { errors, ...resaleAuthorization } = resaleAuthorizationWithError
    const recipientsError = [
      ...(errors.agreementRecipients
        ? (errors.agreementRecipients as Array<Record<string, unknown>>)
        : []),
    ]
    const offerRecipientsWithError = useMemo(
      () =>
        getRecipientErrMapping(
          resaleAuthorization.agreementRecipients || [],
          recipientsError
        ),
      [resaleAuthorization.agreementRecipients, recipientsError]
    )
    const handleClick = () => {
      addOfferRecipient('agreementRecipients')
    }
    const deleteRow = (index: number) => () => {
      deleteOfferRecipient(index, 'agreementRecipients')()
    }
    const updateValue =
      (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        updateRecipientsData(
          index,
          'agreementRecipients'
        )({ key: event.target.id, value: event.target.value })
      }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      handleDetailsChange({
        key: event.target.name as string,
        value: event.target.value as string,
      })
    }
    return (
      <div>
        <Box heading={'Details'} subHeading={''}>
          <div
            className={clsx(
              classes.subHeadingContainer,
              classes.companyDetailSubHead
            )}
          >
            <Typography
              className={classes.subHeading}
              data-testid="details-subheading"
            >
              Authorization details
            </Typography>
            <LabraToolTip
              tooltipPlacement={'right'}
              label={'Brief details about the resale authorization'}
            >
              <InfoOutlined className={classes.infoIcon} />
            </LabraToolTip>
          </div>
          <div className={classes.companyDetailRow}>
            <div className={classes.name} key={`companyName-${randomKey}`}>
              <TextField
                required
                id="companyName"
                name="companyName"
                label="Company name"
                defaultValue={resaleAuthorization?.companyName || ''}
                onChange={handleChange}
                className={classes.textField}
                error={!isEmpty(errors?.companyName)}
                helperText={(errors?.companyName as string) || ''}
                fullWidth
                variant="standard"
                slotProps={{
                  input: { inputProps: { 'data-testid': 'reseller-name' } },
                }}
              />
            </div>
            <div
              className={classes.agreementName}
              key={`agreementName-${randomKey}`}
            >
              <TextField
                required
                id="agreementName"
                name="agreementName"
                label="Authorization name"
                defaultValue={resaleAuthorization?.agreementName || ''}
                onChange={handleChange}
                className={classes.textField}
                error={!isEmpty(errors?.agreementName)}
                helperText={(errors?.agreementName as string) || ''}
                fullWidth
                variant="standard"
                slotProps={{
                  input: { inputProps: { 'data-testid': 'agreement-name' } },
                }}
              />
            </div>
          </div>
          <div
            className={classes.agreementDescription}
            key={`agreementDescription-${randomKey}`}
          >
            <TextField
              id="agreementDescription"
              name="agreementDescription"
              label="Authorization description"
              type="text"
              key="agreementDescription"
              defaultValue={resaleAuthorization.agreementDescription}
              onChange={handleChange}
              className={classes.textField}
              error={!isEmpty(errors?.agreementDescription)}
              helperText={(errors?.agreementDescription as string) || ''}
              fullWidth
              variant="standard"
              slotProps={{
                input: {
                  inputProps: { 'data-testid': 'agreement-description' },
                },
              }}
            />
          </div>
          <div
            className={clsx(
              classes.offerRecipientsContainer,
              classes.offerRecipientSubHead
            )}
          >
            <Typography
              className={classes.subHeading}
              data-testid="offer-recipient"
            >
              Authorization recipients
            </Typography>
            <LabraToolTip
              tooltipPlacement={'right'}
              label={
                'Recipients to whom the resale authorization should be sent.'
              }
            >
              <InfoOutlined className={classes.infoIcon} />
            </LabraToolTip>
          </div>
          <AddRecipients
            offerRecipients={offerRecipientsWithError}
            handleClick={handleClick}
            updateValue={updateValue}
            deleteRow={deleteRow}
            buttonLabel={'Add authorization recipient'}
            isResaleAuthorization={true}
          />
          <div
            className={classes.customEmailMessage}
            key={`customMessage-${randomKey}`}
          >
            <TextField
              required
              id="customMessage"
              name="emailCustomMessage"
              label="Custom email message"
              type="text"
              key="customMessage"
              defaultValue={resaleAuthorization.emailCustomMessage}
              onChange={handleChange}
              className={classes.textField}
              error={!isEmpty(errors?.emailCustomMessage as string)}
              helperText={(errors?.emailCustomMessage as string) || ''}
              fullWidth
              variant="standard"
              slotProps={{
                input: {
                  inputProps: { 'data-testid': 'email-custom-message' },
                },
              }}
            />
          </div>
        </Box>
      </div>
    )
  }
)
